/**
 * Suspend / reactivate a wedding book.
 *
 * CONFIRM-GATE INVARIANT (the Quill convention, and the reason this is a
 * component rather than a window.confirm): NO NETWORK CALL HAPPENS UNTIL THE
 * OPERATOR PRESSES THE CONFIRM BUTTON. Opening the dialog, typing a reason and
 * closing it must all be free of side effects — there is a journey test that
 * asserts fetch was never called on those paths.
 *
 * The reason is REQUIRED to suspend and goes only to admin_audit_log. The
 * couple's activity feed records that the book was suspended and by
 * 'LifeScribe Team', never the note and never the operator's email — which is
 * why the dialog says so out loud, so nobody writes a sentence for the couple
 * here by mistake.
 */

import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Stack,
  Alert,
  Box,
  Typography,
  CircularProgress,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { WeddingStatusChip } from '../WeddingChips';
import type { BookStatus } from '../weddingApi';

export type SuspendMode = 'suspend' | 'reactivate';

interface Props {
  open: boolean;
  mode: SuspendMode;
  weddingCode: string;
  currentStatus: BookStatus;
  /** Where reactivate will land — the server decides, we only preview it. */
  restoreTarget?: BookStatus | null;
  submitting?: boolean;
  error?: string | null;
  onClose: () => void;
  onConfirm: (reason: string) => void;
}

const MAX_REASON = 300;

const SuspendReactivateDialog: React.FC<Props> = ({
  open,
  mode,
  weddingCode,
  currentStatus,
  restoreTarget,
  submitting = false,
  error,
  onClose,
  onConfirm,
}) => {
  const [reason, setReason] = React.useState('');

  // A reason from a previous open must never be carried into the next one.
  React.useEffect(() => {
    if (open) setReason('');
  }, [open, mode]);

  const isSuspend = mode === 'suspend';
  const nextStatus: BookStatus = isSuspend ? 'suspended' : (restoreTarget || 'collecting');
  const canConfirm = !submitting && (!isSuspend || reason.trim().length > 0);

  return (
    <Dialog open={open} onClose={submitting ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isSuspend ? `Suspend ${weddingCode}?` : `Reactivate ${weddingCode}?`}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <WeddingStatusChip status={currentStatus} />
            <ArrowForwardIcon fontSize="small" color="disabled" />
            <WeddingStatusChip status={nextStatus} />
          </Box>

          <DialogContentText component="div">
            {isSuspend ? (
              <>
                Guests will no longer be able to open the book or submit a story, and no
                invitation or reminder can be sent while it is suspended.
              </>
            ) : (
              <>
                The book goes back to the status it held before it was suspended
                {restoreTarget ? '' : ' (or to “collecting” if that is no longer known)'}, and
                guests can contribute again.
              </>
            )}
          </DialogContentText>

          {isSuspend && (
            <TextField
              label="Reason (internal)"
              placeholder="Why is this book being suspended?"
              multiline
              minRows={3}
              fullWidth
              required
              value={reason}
              onChange={(e) => setReason(e.target.value.slice(0, MAX_REASON))}
              helperText={`${reason.length}/${MAX_REASON} — recorded in the admin audit log only. The couple sees that the book was suspended by “LifeScribe Team”, not this note.`}
              disabled={submitting}
            />
          )}

          <Alert severity="info">
            The couple&rsquo;s own dashboard can take up to a minute to catch up — it caches the
            book for 60 seconds.
          </Alert>

          {error && <Alert severity="error">{error}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color={isSuspend ? 'error' : 'primary'}
          disabled={!canConfirm}
          onClick={() => onConfirm(reason.trim())}
          startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isSuspend ? 'Suspend book' : 'Reactivate book'}
        </Button>
      </DialogActions>
      {/* Screen-reader-only summary of what confirming will do. */}
      <Typography component="span" sx={{ position: 'absolute', left: -9999 }}>
        {isSuspend
          ? `Confirming suspends ${weddingCode}.`
          : `Confirming restores ${weddingCode} to ${nextStatus}.`}
      </Typography>
    </Dialog>
  );
};

export default SuspendReactivateDialog;
